import {
  useCommercialOfferCustomShippingAddressCreateMutation,
  useCommercialOfferCustomShippingAddressUpdateMutation,
} from 'api';
import { Form } from 'components';
import { useParams } from 'react-router-dom';
import { getUpdatedPayload } from 'utils/helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { ICustomAddressAddEdit } from './index';
import { useCountryMapping } from 'hooks/useCountryMapping';
import { CountryStateType } from 'models/Country';

export const useAddressUpdate = (props: ICustomAddressAddEdit) => {
  const { modalState, dataToEdit, onModalStateChange, onSuccess } = props;
  const [states, setStates] = useState<CountryStateType[]>([]);
  const [form] = Form.useForm();
  const { commercialOfferId } = useParams() as { commercialOfferId: string };

  const { handleError, handleSuccess } = useNotificationMessages();
  const { countryMapping } = useCountryMapping();
  const countryData = useMemo(() => countryMapping[dataToEdit?.countryId], [dataToEdit?.countryId, countryMapping]);
  const [clientCustomAddressCreate, { isLoading: createIsLoading }] =
    useCommercialOfferCustomShippingAddressCreateMutation();
  const [clientCustomAddressUpdate, { isLoading: updateIsLoading }] =
    useCommercialOfferCustomShippingAddressUpdateMutation();

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setStates([]);
  }, [form, onModalStateChange]);

  const onFinish = useCallback(async () => {
    const payload = getUpdatedPayload(form.getFieldsValue(), dataToEdit);
    if (dataToEdit) {
      payload.id = dataToEdit.id;
    } else {
      payload.commercialOfferId = commercialOfferId;
    }
    try {
      const { data, error }: any = await (dataToEdit ? clientCustomAddressUpdate : clientCustomAddressCreate)(payload);
      if (data?.success) {
        onSuccess();
        handleSuccess(`The custom shipping address successfully ${dataToEdit ? 'updated' : 'created'}.`);
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [
    clientCustomAddressCreate,
    clientCustomAddressUpdate,
    onCancel,
    commercialOfferId,
    handleSuccess,
    handleError,
    dataToEdit,
    form,
    onSuccess,
  ]);

  const handleCountryChange = useCallback(
    (_: string, value: Record<string, any>) => {
      setStates(value.states || []);
      form.setFieldsValue({ state: '' });
    },
    [form]
  );

  useEffect(() => {
    if (countryData?.states.length) {
      setStates(countryData.states);
    }
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [form, dataToEdit, modalState, countryData]);

  return {
    form,
    isLoading: updateIsLoading || createIsLoading,
    states,
    onFinish,
    onCancel,
    handleCountryChange,
  };
};
